import { useParams, Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRoles } from "@/hooks/useCommonQueries";
import InputField from "@/components/form/InputField";
import { Form, Formik } from "formik";
import { userService } from "@/services/breeze/UserService";
import SelectField from "@/components/form/SelectField";
import withPermissionRequired from "@/hoc/withPermissionRequired";
import PageTemplate from "@/components/page-template/PageTemplate";
import toast from "react-hot-toast";
import {
  integrationIcons,
  integrationBrandColors,
} from "@/components/workflow/IntegrationSelector";
import {
  Box,
  Flex,
  Text,
  Heading,
  Grid,
  Button,
  Card,
  Table,
  Badge,
} from "@radix-ui/themes";
import { authService } from "@/services/breeze/AuthService";
import { useAuth } from "@/providers/AuthProvider";
import { anythinkPayService } from "@/services/breeze/AnythinkPayService";
import { SubscriptionStatusBadge } from "@/components/subscriptions/SubscriptionStatusBadge";
import { useUsersRedeemedCodes } from "@/hooks/useAnythinkPayOffers";
import UserBalanceCard from "@/components/anythinkpay/UserBalanceCard";
import RegisteredPushDevicesCard from "@/components/push/RegisteredPushDevicesCard";
import UserProfileCard from "@/components/users/UserProfileCard";
import UserGroupsCard from "@/components/users/UserGroupsCard";
import { formatDate } from "@/utils/date";
import { SkeletonPage, SkeletonTableRows } from "@/components/ui/Skeleton";

const EditUserPage = withPermissionRequired(
  "anythink_users:update",
  function EditUserPage() {
    const params = useParams();
    const queryClient = useQueryClient();
    const { currentTenant, currentUser, safeNavigate } = useAuth();
    const viewedUserId = parseInt(params.userId as string);
    const canViewSubscriptions =
      !!currentUser &&
      (currentUser.id === viewedUserId || currentUser.is_tenant_administrator);

    const {
      data: roles,
      isLoading: isRolesLoading,
      error: rolesError,
    } = useRoles();

    const {
      data: user,
      isLoading: isUserLoading,
      error: userError,
    } = useQuery({
      queryKey: ["user", params.userId],
      queryFn: async () => {
        return userService.getUser(viewedUserId);
      },
      refetchOnWindowFocus: true,
    });

    const { data: subscriptions, isLoading: isSubscriptionsLoading } = useQuery(
      {
        queryKey: ["user-subscriptions", params.userId],
        queryFn: () => anythinkPayService.getSubscriptionsByUser(viewedUserId),
        enabled: !!user && canViewSubscriptions,
      },
    );

    // Promo / referral codes this user has redeemed. Hits the batch endpoint with a single ID;
    // shows up on the user's detail card alongside subscriptions so admins can see redemption
    // history without crowding the project Users list.
    const redeemedCodesQuery = useUsersRedeemedCodes(user ? [viewedUserId] : []);
    const redeemedCodes = redeemedCodesQuery.data?.[viewedUserId] ?? [];

    const { data: credentials } = useQuery({
      queryKey: ["user-credentials", params.userId],
      queryFn: () => userService.getUserCredentials(viewedUserId),
      enabled: !!user,
    });

    const { mutate: updateUser, isPending: isUpdatingUser } = useMutation({
      mutationFn: (data: {
        first_name: string;
        last_name: string;
        role_id: number;
      }) => userService.updateUser(viewedUserId, data),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user", params.userId],
        });
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
        safeNavigate(`/org/${currentTenant?.id}/settings/users`);
      },
    });

    const { mutate: sendPasswordReset, isPending: isSendingPasswordReset } =
      useMutation({
        mutationFn: (email: string) => authService.sendPasswordReset(email),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["user", params.userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["users"],
          });
        },
      });

    // const { mutate: sendMagicLink, isPending: isSendingMagicLink } =
    //   useMutation({
    //     mutationFn: (email: string) => AuthService.sendMagicLink(email),
    //     onSuccess: () => {
    //       queryClient.invalidateQueries({
    //         queryKey: ["user", params.userId],
    //       });
    //       queryClient.invalidateQueries({
    //         queryKey: ["users"],
    //       });
    //     },
    //   });

    const {
      mutate: sendConfirmationEmail,
      isPending: isSendingConfirmationEmail,
    } = useMutation({
      mutationFn: (userId: number) => userService.sendConfirmationEmail(userId),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: ["user", params.userId],
        });
        queryClient.invalidateQueries({
          queryKey: ["users"],
        });
      },
    });

    const { mutate: sendInvitationEmail, isPending: isSendingInvitationEmail } =
      useMutation({
        mutationFn: (userId: number) => userService.sendInvitationEmail(userId),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["user", params.userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["users"],
          });
        },
      });

    const { mutate: markAsConfirmed, isPending: isMarkingAsConfirmed } =
      useMutation({
        mutationFn: (userId: number) => userService.markAsConfirmed(userId),
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["user", params.userId],
          });
          queryClient.invalidateQueries({
            queryKey: ["users"],
          });
        },
      });

    if (isUserLoading || isRolesLoading) {
      return <SkeletonPage title="User" />;
    }

    if (userError || rolesError) {
      return (
        <Box p="4">
          <Text color="red">Error loading user</Text>
        </Box>
      );
    }

    if (!user) {
      return (
        <Box p="4">
          <Text size="2" color="gray">
            No data found
          </Text>
        </Box>
      );
    }

    // Prevent editing central users
    if (user.is_central_user) {
      return (
        <PageTemplate title={`${user.first_name} ${user.last_name}`}>
          <Card>
            <Box p="6">
              <Heading size="6" mb="4">
                MyAnythink User
              </Heading>
              <Text size="3" mb="4" className="block">
                This user is centrally managed through MyAnythink (
                <a
                  href="https://my.anythink.cloud"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{
                    color: "var(--accent-11)",
                    textDecoration: "underline",
                  }}
                >
                  my.anythink.cloud
                </a>
                ).
              </Text>
              <Text size="3" mb="4" className="block">
                All user information, roles, and permissions for MyAnythink
                users must be managed through the MyAnythink portal. No changes
                can be made from this interface.
              </Text>
              <Button
                onClick={() =>
                  safeNavigate(`/org/${currentTenant?.id}/settings/users`)
                }
              >
                Back to Users
              </Button>
            </Box>
          </Card>
        </PageTemplate>
      );
    }

    return (
      <PageTemplate title={`${user.first_name} ${user.last_name}`}>
        <Grid columns={{ initial: "1", md: "2" }} gap="6">
          <Card>
            <Box p="2">
              <Heading size="4" weight="medium" mb="4" mt="0">
                User Information
              </Heading>
              <Flex direction="column" gap="4">
                <Box>
                  <Text
                    size="2"
                    weight="medium"
                    color="gray"
                    mb="1"
                    className="block"
                  >
                    Email
                  </Text>
                  <Text size="2">{user.email}</Text>
                </Box>
                <Box>
                  <Text
                    size="2"
                    weight="medium"
                    color="gray"
                    mb="1"
                    className="block"
                  >
                    Current Role
                  </Text>
                  <Text size="2">
                    {roles?.find((r) => r.id === user.role_id)?.name}
                  </Text>
                </Box>
                <Box>
                  <Text
                    size="2"
                    weight="medium"
                    color="gray"
                    mb="1"
                    className="block"
                  >
                    Status
                  </Text>
                  <Text size="2">
                    {user.is_active ? (
                      <Text color="green" weight="medium">
                        Active
                      </Text>
                    ) : (
                      <Text color="orange" weight="medium">
                        Invite Pending
                      </Text>
                    )}
                  </Text>
                </Box>
                <Box>
                  <Text
                    size="2"
                    weight="medium"
                    color="gray"
                    mb="1"
                    className="block"
                  >
                    Authentication
                  </Text>
                  {user.auth_provider ? (
                    (() => {
                      const Icon = integrationIcons[user.auth_provider!];
                      const brand = integrationBrandColors[user.auth_provider!];
                      return (
                        <Flex align="center" gap="2">
                          {Icon && (
                            <Box
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                backgroundColor: brand?.bg || "var(--gray-9)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                              }}
                            >
                              <Box
                                style={{
                                  transform: "scale(0.65)",
                                  display: "flex",
                                }}
                              >
                                <Icon />
                              </Box>
                            </Box>
                          )}
                          <Text
                            size="2"
                            style={{ textTransform: "capitalize" }}
                          >
                            {user.auth_provider}
                          </Text>
                        </Flex>
                      );
                    })()
                  ) : (
                    <Text size="2">Password</Text>
                  )}
                </Box>
                <Box>
                  <Text
                    size="2"
                    weight="medium"
                    color="gray"
                    mb="1"
                    className="block"
                  >
                    Last Login
                  </Text>
                  <Text size="2">
                    {user.last_login_at ? (
                      <Text>
                        {new Date(user.last_login_at).toLocaleDateString()}
                      </Text>
                    ) : (
                      <Text>Never</Text>
                    )}
                  </Text>
                </Box>
                <Flex gap="3" mt="2" wrap="wrap">
                  {user.invite_token ? (
                    <Button
                      onClick={() => sendInvitationEmail(user.id)}
                      loading={isSendingInvitationEmail}
                    >
                      Resend Invitation Email
                    </Button>
                  ) : !user.confirmed_at ? (
                    <>
                      <Button
                        onClick={() => sendConfirmationEmail(user.id)}
                        loading={isSendingConfirmationEmail}
                      >
                        Resend Confirmation Email
                      </Button>
                      <Button
                        onClick={() => markAsConfirmed(user.id)}
                        loading={isMarkingAsConfirmed}
                      >
                        Mark as Confirmed
                      </Button>
                    </>
                  ) : (
                    <Button
                      onClick={() => sendPasswordReset(user.email)}
                      loading={isSendingPasswordReset}
                    >
                      Send Password Reset
                    </Button>
                  )}
                </Flex>
              </Flex>
            </Box>
          </Card>

          {/* Invite Status Card */}
          {user.invite_token && (
            <Card>
              <Box p="2">
                <Heading size="4" weight="medium" mb="4" mt="0">
                  Invite Status
                </Heading>
                <Flex direction="column" gap="4">
                  <Box>
                    <Text
                      size="2"
                      weight="medium"
                      color="gray"
                      mb="1"
                      className="block"
                    >
                      Invite Status
                    </Text>
                    <Text size="2">
                      <Text color="orange" weight="medium">
                        Pending Acceptance
                      </Text>
                    </Text>
                  </Box>

                  {user.invite_token_expires_at && (
                    <Box>
                      <Text
                        size="2"
                        weight="medium"
                        color="gray"
                        mb="1"
                        className="block"
                      >
                        Expires
                      </Text>
                      <Text size="2">
                        {new Date(
                          user.invite_token_expires_at,
                        ).toLocaleDateString()}{" "}
                        {new Date(
                          user.invite_token_expires_at,
                        ).toLocaleTimeString()}
                      </Text>
                    </Box>
                  )}

                  <Box>
                    <Text size="2" weight="medium" color="gray" mb="1">
                      Invite Link
                    </Text>
                    <Flex gap="2" mt="1">
                      <Button
                        variant="outline"
                        size="1"
                        onClick={() => {
                          const inviteUrl = `${window.location.origin}/org/${currentTenant?.id}/auth/invites/${user.invite_token}?org_id=${currentTenant?.id}`;
                          navigator.clipboard.writeText(inviteUrl);
                          toast.success("Invite link copied to clipboard");
                        }}
                      >
                        Copy Invite Link
                      </Button>
                    </Flex>
                  </Box>
                </Flex>
              </Box>
            </Card>
          )}

          <Card>
            <Box p="2">
              <Heading size="4" weight="medium" mb="4" mt="0">
                Edit User
              </Heading>

              <Formik
                initialValues={{
                  first_name: user.first_name,
                  last_name: user.last_name,
                  role_id: user.role_id,
                }}
                onSubmit={(values) => {
                  updateUser(values);
                }}
              >
                <Form>
                  <Flex direction="column" gap="4">
                    <Flex gap="4">
                      <InputField
                        name="first_name"
                        label="First Name"
                        required
                      />
                      <InputField name="last_name" label="Last Name" required />
                    </Flex>
                    <SelectField
                      name="role_id"
                      label="Role"
                      required
                      type="number"
                    >
                      {roles
                        ?.filter(
                          (role) => role.is_active && !role.is_administrator,
                        )
                        .map((role) => (
                          <SelectField.Item key={role.id} value={role.id}>
                            {role.name}
                          </SelectField.Item>
                        ))}
                    </SelectField>

                    <Flex>
                      <Button type="submit" loading={isUpdatingUser}>
                        Update User
                      </Button>
                    </Flex>
                  </Flex>
                </Form>
              </Formik>
            </Box>
          </Card>

          <Box style={{ gridColumn: "1 / -1" }}>
            <UserProfileCard userId={viewedUserId} />
          </Box>

          {canViewSubscriptions && (
            <Card style={{ gridColumn: "1 / -1" }}>
              <Box p="2">
                <Heading size="4" weight="medium" mb="4" mt="0">
                  Subscriptions
                </Heading>
                {isSubscriptionsLoading ? (
                  <Text size="2" color="gray">
                    Loading subscriptions…
                  </Text>
                ) : subscriptions && subscriptions.length > 0 ? (
                  <Table.Root>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell></Table.ColumnHeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {subscriptions.map((sub) => (
                        <Table.Row key={sub.id}>
                          <Table.Cell>
                            <Text size="2" weight="medium">
                              {sub.name}
                            </Text>
                          </Table.Cell>
                          <Table.Cell>
                            <SubscriptionStatusBadge
                              status={sub.status}
                              showIcon
                            />
                          </Table.Cell>
                          <Table.Cell>
                            <Button variant="ghost" size="1" asChild>
                              <Link
                                to={`/org/${currentTenant?.id}/integrations/anythinkpay/subscriptions/${sub.id}`}
                              >
                                View
                              </Link>
                            </Button>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text size="2" color="gray">
                    This user has no subscriptions.
                  </Text>
                )}
              </Box>
            </Card>
          )}

          {canViewSubscriptions && (
            <Box style={{ gridColumn: "1 / -1" }}>
              <UserBalanceCard userId={viewedUserId} />
            </Box>
          )}

          {canViewSubscriptions && (
            <Card style={{ gridColumn: "1 / -1" }}>
              <Box p="2">
                <Heading size="4" weight="medium" mb="4" mt="0">
                  Redeemed codes
                </Heading>
                {redeemedCodesQuery.isLoading ? (
                  <SkeletonTableRows rows={3} columns={3} />
                ) : redeemedCodes.length > 0 ? (
                  <Table.Root>
                    <Table.Header>
                      <Table.Row>
                        <Table.ColumnHeaderCell>Code</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>Programme / offer</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>Reward status</Table.ColumnHeaderCell>
                        <Table.ColumnHeaderCell>Redeemed</Table.ColumnHeaderCell>
                      </Table.Row>
                    </Table.Header>
                    <Table.Body>
                      {redeemedCodes.map((c) => (
                        <Table.Row key={c.redemption_id}>
                          <Table.Cell>
                            <Badge size="1" variant="soft" color="green" style={{ fontFamily: "monospace" }}>
                              {c.slug}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2">{c.offer_name ?? "—"}</Text>
                          </Table.Cell>
                          <Table.Cell>
                            <Badge
                              size="1"
                              variant="soft"
                              color={
                                c.redeemer_reward_status === "applied"
                                  ? "green"
                                  : c.redeemer_reward_status === "pending"
                                  ? "amber"
                                  : c.redeemer_reward_status === "failed"
                                  ? "red"
                                  : "gray"
                              }
                            >
                              {c.redeemer_reward_status}
                            </Badge>
                          </Table.Cell>
                          <Table.Cell>
                            <Text size="2" color="gray">{formatDate(c.redeemed_at)}</Text>
                          </Table.Cell>
                        </Table.Row>
                      ))}
                    </Table.Body>
                  </Table.Root>
                ) : (
                  <Text size="2" color="gray">
                    This user hasn't redeemed any codes.
                  </Text>
                )}
              </Box>
            </Card>
          )}

          {/* Secrets & Connections */}
          <Card style={{ gridColumn: "1 / -1" }}>
            <Box p="2">
              <Heading size="4" weight="medium" mb="4" mt="0">
                Secrets & Connections
              </Heading>
              {credentials &&
              (credentials.connections?.length > 0 ||
                credentials.secrets?.length > 0) ? (
                <Table.Root>
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeaderCell>Type</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Name</Table.ColumnHeaderCell>
                      <Table.ColumnHeaderCell>Status</Table.ColumnHeaderCell>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {credentials.connections?.map((conn, i) => (
                      <Table.Row key={`conn-${i}`}>
                        <Table.Cell>
                          <Badge variant="soft" color="blue" size="1">
                            Connection
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Flex align="center" gap="2">
                            {(() => {
                              const Icon = integrationIcons[conn.provider];
                              const brand =
                                integrationBrandColors[conn.provider];
                              return Icon ? (
                                <Box
                                  style={{
                                    width: 18,
                                    height: 18,
                                    borderRadius: 3,
                                    backgroundColor:
                                      brand?.bg || "var(--gray-9)",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Box
                                    style={{
                                      transform: "scale(0.6)",
                                      display: "flex",
                                    }}
                                  >
                                    <Icon />
                                  </Box>
                                </Box>
                              ) : null;
                            })()}
                            <Text size="2">{conn.display_name}</Text>
                          </Flex>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant="soft" color="green" size="1">
                            Connected
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                    {credentials.secrets?.map((secret, i) => (
                      <Table.Row key={`secret-${i}`}>
                        <Table.Cell>
                          <Badge variant="soft" color="purple" size="1">
                            Secret
                          </Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Text size="2">{secret.field_label}</Text>
                          <Text size="1" color="gray">
                            {" "}
                            ({secret.entity_name})
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant="soft" color="green" size="1">
                            Configured
                          </Badge>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              ) : (
                <Text size="2" color="gray">
                  This user has no secrets or connections configured.
                </Text>
              )}
            </Box>
          </Card>
        </Grid>

        <Box mt="6">
          <UserGroupsCard userId={user.id} />
        </Box>

        <Box mt="6">
          <RegisteredPushDevicesCard
            ownerEntityId={user.id}
            emptyHint="No push devices registered for this user yet. The app needs to register an FCM token (Anythink-managed) or write one into the configured entity field."
          />
        </Box>
      </PageTemplate>
    );
  },
);

export default EditUserPage;
